import { createContext, startTransition, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSideMenuContext } from './sideMenuContext';
import { useNoteDispatch, useNoteState } from './noteContexts/noteContext';

const NavigateContext = createContext(undefined);

const ALLOWED_TYPES = ['visual', 'kinesthic', 'audio', 'read'];

const normalizeView = (value) => (String(value || '').toLowerCase() === 'adapted' ? 'adapted' : 'editor');

const normalizeType = (value) => {
	const normalized = String(value || 'visual').toLowerCase();
	return ALLOWED_TYPES.includes(normalized) ? normalized : 'visual';
};

const getContentIndexFromType = (noteType) => {
	const normalized = normalizeType(noteType);
	if (normalized === 'kinesthic') return 1;
	if (normalized === 'audio') return 2;
	if (normalized === 'read') return 3;
	return 0;
};

const getTypeFromContentIndex = (index) => {
	if (index === 1) return 'kinesthic';
	if (index === 2) return 'audio';
	if (index === 3) return 'read';
	return 'visual';
};

const getRouteStateFromPathname = (pathname) => {
	const value = String(pathname || '').trim();
	const match = value.match(/^\/note-(editor|adapted)(?:\/([^/?#]+))?(?:\/([^/?#]+))?/i);
	if (!match) {
		return { view: null, noteId: null, noteType: null };
	}

	return {
		view: normalizeView(match[1]),
		noteId: match[2] ? decodeURIComponent(match[2]) : null,
		noteType: match[3] ? normalizeType(decodeURIComponent(match[3])) : null,
	};
};

const buildNotePath = (view, id, noteType) => {
	const normalizedView = normalizeView(view);
	const normalizedId = String(id || '').trim();
	if (!normalizedId) {
		return normalizedView === 'adapted' ? '/note-adapted' : '/note-editor';
	}

	if (normalizedView === 'adapted') {
		return `/note-adapted/${encodeURIComponent(normalizedId)}/${encodeURIComponent(normalizeType(noteType))}`;
	}

	return `/note-editor/${encodeURIComponent(normalizedId)}`;
};

export const NavigateProvider = ({ children }) => {
	const navigate = useNavigate();
	const location = useLocation();
	const routeState = useMemo(() => getRouteStateFromPathname(location.pathname), [location.pathname]);
	const routeIsNote = Boolean(routeState.view);
	const locationPathnameRef = useRef(location.pathname);
	const routeNoteIdRef = useRef(routeState.noteId);
	const routeNoteTypeRef = useRef(routeState.noteType);
	const currentNoteTypeRef = useRef('visual');
	const ifExpectSourceContentParsingRef = useRef(false);

	const {
		set_if_show_view_option,
		set_if_show_content_option,
		set_index_btn_main_menus_selected,
		set_index_view_btn_selected,
		set_index_content_btn_selected,
	} = useSideMenuContext();

	const {
		if_note_opened,
		if_expect_source_content_parsing,
		note_id,
		current_note_view,
		current_note_type,
	} = useNoteState();

	const {
		resetNoteState,
		setIf_note_opened,
		flushCurrentNoteToDatabase,
		setCurrent_note_view,
		setCurrent_note_type,
	} = useNoteDispatch();

	useEffect(() => {
		locationPathnameRef.current = location.pathname;
		routeNoteIdRef.current = routeState.noteId;
		routeNoteTypeRef.current = routeState.noteType;
		currentNoteTypeRef.current = current_note_type || 'visual';
		ifExpectSourceContentParsingRef.current = Boolean(if_expect_source_content_parsing);
	}, [current_note_type, if_expect_source_content_parsing, location.pathname, routeState.noteId, routeState.noteType]);

	const projectUiForHome = useCallback(() => {
		startTransition(() => {
			set_index_btn_main_menus_selected(0);
			set_if_show_view_option(false);
			set_if_show_content_option(false);
			set_index_view_btn_selected(-1);
			set_index_content_btn_selected(-1);
		});
	}, [
		set_if_show_content_option,
		set_if_show_view_option,
		set_index_btn_main_menus_selected,
		set_index_content_btn_selected,
		set_index_view_btn_selected,
	]);

	const projectUiForNonNotePage = useCallback((nextPathname = locationPathnameRef.current) => {
		startTransition(() => {
		if (nextPathname === '/') {
			set_index_btn_main_menus_selected(0);
		} else if (nextPathname === '/my-notes') {
			set_index_btn_main_menus_selected(1);
		} else {
			set_index_btn_main_menus_selected(-1);
		}
		set_if_show_view_option(false);
		set_if_show_content_option(false);
		set_index_view_btn_selected(-1);
		set_index_content_btn_selected(-1);
		});
	}, [
		set_if_show_content_option,
		set_if_show_view_option,
		set_index_btn_main_menus_selected,
		set_index_content_btn_selected,
		set_index_view_btn_selected,
	]);

	const projectUiForNoteRoute = useCallback((view, noteType) => {
		startTransition(() => {
			set_index_btn_main_menus_selected(-1);
			set_if_show_view_option(true);
			set_if_show_content_option(true);

			const expectedViewIndex = view === 'adapted' ? 1 : 0;
			set_index_view_btn_selected(expectedViewIndex);

			if (view === 'adapted') {
				set_index_content_btn_selected(getContentIndexFromType(noteType));
				return;
			}

			set_index_content_btn_selected(-1);
		});
	}, [
		set_if_show_content_option,
		set_if_show_view_option,
		set_index_btn_main_menus_selected,
		set_index_content_btn_selected,
		set_index_view_btn_selected,
	]);

	const openNoteRoute = useCallback((nextView, nextType) => {
		const view = normalizeView(nextView);
		const effectiveId = note_id || routeNoteIdRef.current;
		const type = normalizeType(nextType || routeNoteTypeRef.current || currentNoteTypeRef.current || 'visual');
		const activeRouteState = getRouteStateFromPathname(locationPathnameRef.current);
		const currentlyOnNoteRoute = Boolean(activeRouteState.view);
		const activeView = normalizeView(activeRouteState.view || current_note_view);
		const activeType = normalizeType(activeRouteState.noteType || currentNoteTypeRef.current || 'visual');
		const isSwitchingBetweenNoteViews = currentlyOnNoteRoute
			&& (activeView !== view || (view === 'adapted' && activeType !== type));

		if (isSwitchingBetweenNoteViews) {
			flushCurrentNoteToDatabase().catch((error) => {
				console.error('View-switch flush failed:', error);
			});
		}

		startTransition(() => {
			setIf_note_opened(true);
			setCurrent_note_view(view);
			if (view === 'adapted') {
				setCurrent_note_type(type);
			}
		});

		projectUiForNoteRoute(view, type);

		if (!effectiveId && ifExpectSourceContentParsingRef.current) {
			return;
		}

		const targetPath = buildNotePath(view, effectiveId, type);
		if (locationPathnameRef.current !== targetPath) {
			navigate(targetPath, { replace: true });
		}
	}, [
		current_note_view,
		flushCurrentNoteToDatabase,
		navigate,
		note_id,
		projectUiForNoteRoute,
		setCurrent_note_type,
		setCurrent_note_view,
		setIf_note_opened,
	]);

	const setValuesAsHomeView = useCallback(() => {
		flushCurrentNoteToDatabase().catch((error) => {
			console.error('Home flush failed:', error);
		});
		startTransition(() => {
			resetNoteState();
			setIf_note_opened(false);
			projectUiForHome();
		});
		if (locationPathnameRef.current !== '/') {
			navigate('/', { replace: true });
		}
	}, [flushCurrentNoteToDatabase, navigate, projectUiForHome, resetNoteState, setIf_note_opened]);

	const setValuesAsMyNotesView = useCallback(() => {
		flushCurrentNoteToDatabase().catch((error) => {
			console.error('My-notes flush failed:', error);
		});
		startTransition(() => {
			resetNoteState();
			setIf_note_opened(false);
			set_index_btn_main_menus_selected(1);
			set_if_show_view_option(false);
			set_if_show_content_option(false);
			set_index_view_btn_selected(-1);
			set_index_content_btn_selected(-1);
		});
		if (locationPathnameRef.current !== '/my-notes') {
			navigate('/my-notes', { replace: true });
		}
	}, [
		flushCurrentNoteToDatabase,
		navigate,
		resetNoteState,
		setIf_note_opened,
		set_if_show_content_option,
		set_if_show_view_option,
		set_index_btn_main_menus_selected,
		set_index_content_btn_selected,
		set_index_view_btn_selected,
	]);

	const setValuesAsSettingsView = useCallback(() => {
		flushCurrentNoteToDatabase().catch((error) => {
			console.error('Settings flush failed:', error);
		});
		startTransition(() => {
			resetNoteState();
			setIf_note_opened(false);
			set_index_btn_main_menus_selected(-1);
			set_if_show_view_option(false);
			set_if_show_content_option(false);
			set_index_view_btn_selected(-1);
			set_index_content_btn_selected(-1);
		});
		if (locationPathnameRef.current !== '/settings') {
			navigate('/settings', { replace: true });
		}
	}, [
		flushCurrentNoteToDatabase,
		navigate,
		resetNoteState,
		setIf_note_opened,
		set_if_show_content_option,
		set_if_show_view_option,
		set_index_btn_main_menus_selected,
		set_index_content_btn_selected,
		set_index_view_btn_selected,
	]);

	const setValuesAsSettingsThemeView = useCallback(() => {
		flushCurrentNoteToDatabase().catch((error) => {
			console.error('Settings-theme flush failed:', error);
		});
		startTransition(() => {
			resetNoteState();
			setIf_note_opened(false);
			set_index_btn_main_menus_selected(-1);
			set_if_show_view_option(false);
			set_if_show_content_option(false);
			set_index_view_btn_selected(-1);
			set_index_content_btn_selected(-1);
		});
		if (locationPathnameRef.current !== '/settings' || window.location.hash !== '#theme') {
			navigate('/settings#theme', { replace: true });
		}
	}, [
		flushCurrentNoteToDatabase,
		navigate,
		resetNoteState,
		setIf_note_opened,
		set_if_show_content_option,
		set_if_show_view_option,
		set_index_btn_main_menus_selected,
		set_index_content_btn_selected,
		set_index_view_btn_selected,
	]);

	const setValuesAsEditorView = useCallback(async () => {
		await openNoteRoute('editor', 'visual');
	}, [openNoteRoute]);

	const setValuesAsAdaptedView = useCallback(async () => {
		await openNoteRoute('adapted', routeNoteTypeRef.current || currentNoteTypeRef.current || 'visual');
	}, [openNoteRoute]);

	const setValuesAsAdaptedType = useCallback(async (nextTypeOrIndex) => {
		const targetType = typeof nextTypeOrIndex === 'number'
			? getTypeFromContentIndex(nextTypeOrIndex)
			: normalizeType(nextTypeOrIndex);
		await openNoteRoute('adapted', targetType);
	}, [openNoteRoute]);

	useEffect(() => {
		if (!routeIsNote) {
			projectUiForNonNotePage(location.pathname);
			startTransition(() => {
				setIf_note_opened(false);
			});
			return;
		}

		const routeView = normalizeView(routeState.view);
		const routeType = normalizeType(routeState.noteType || currentNoteTypeRef.current || 'visual');

		startTransition(() => {
			setIf_note_opened(true);
			setCurrent_note_view(routeView);
			if (routeView === 'adapted') {
				setCurrent_note_type(routeType);
			}
			projectUiForNoteRoute(routeView, routeType);
		});
	}, [
		projectUiForNonNotePage,
		projectUiForNoteRoute,
		routeIsNote,
		location.pathname,
		routeState.noteType,
		routeState.view,
		setCurrent_note_type,
		setCurrent_note_view,
		setIf_note_opened,
	]);

	const value = useMemo(
		() => ({
			setValuesAsHomeView,
			setValuesAsMyNotesView,
			setValuesAsSettingsView,
			setValuesAsSettingsThemeView,
			setValuesAsEditorView,
			setValuesAsAdaptedView,
			setValuesAsAdaptedType,
		}),
		[
			setValuesAsHomeView,
			setValuesAsMyNotesView,
			setValuesAsSettingsView,
			setValuesAsSettingsThemeView,
			setValuesAsEditorView,
			setValuesAsAdaptedView,
			setValuesAsAdaptedType,
		]
	);

	return <NavigateContext.Provider value={value}>{children}</NavigateContext.Provider>;
};

export const useNavigateContext = () => {
	const context = useContext(NavigateContext);
	if (!context) {
		throw new Error('useNavigateContext must be used within a NavigateProvider.');
	}
	return context;
};

export default NavigateContext;
